import type { Failure } from "@/modules/shared/failure";
import { renderFieldErrors } from "@/modules/shared/fieldCopy";
import { SYSTEM_ERROR, type FormState } from "@/modules/shared/forms";

/**
 * The **web's** copy for every code a Catalog command core can refuse with,
 * and the one place a `Failure` becomes a `FormState` (R22 T4).
 *
 * A separate module from the `"use server"` adapters because every export in
 * one of those must be an async function, so a pure lookup could neither live
 * nor be unit-tested there. `./catalogCopy.test.ts` holds it to the promise
 * that no code a core returns falls through to the generic apology.
 *
 * **Every sentence is unchanged from before the cores existed.** T4 moved where
 * a sentence is chosen, never which sentence it is.
 */
export function catalogCopyFor(failure: Failure): string {
  switch (failure.code) {
    case "service_not_found":
      return "This service no longer exists.";
    case "practitioner_not_found":
      return "This practitioner no longer exists.";
    case "practitioner_already_archived":
      return "This practitioner is already archived.";
    // H26. The *archive* screen says this differently, and says so itself: see
    // `practitioners.ts`, where the button pressed decides the wording.
    case "last_active_manager":
      return "You can't remove the last manager. A business always needs one manager who can manage it.";
    case "already_member":
      return "They are already on your team.";
    case "services_required":
      // The tools gate (J2#2): practitioner management needs one Service first.
      return "Add your first service to unlock practitioner management.";
    // H59. Only a hand-made request reaches either: the screen uploads every
    // photo into the clinic's own folder and stops adding at ten.
    case "photo_url_invalid":
      return "Those photos couldn't be saved. Try again.";
    case "photos_too_many":
      return "A clinic can show up to 10 photos.";
    case "rate_limited":
      // Provisioning spends money through the WhatsApp sender, so it sits
      // behind the same limiter `resendOtp` does (H44).
      return "Too many people added right now. Please try again shortly.";
    case "malformed_input":
      // Reached only when a form posts without an id it should always carry,
      // which no screen does. There is nothing useful to tell somebody about a
      // field they cannot see, so it keeps the generic apology it always had.
      // Field-level rejections never arrive here: they carry `fieldErrors` and
      // `formStateFor` renders them inline instead.
      return SYSTEM_ERROR;
    default:
      return SYSTEM_ERROR;
  }
}

/**
 * Turn a core's `Failure` into the `FormState` a screen renders.
 *
 * **The split between inline and banner is a property of the failure, not of
 * the command.** Anything carrying `fieldErrors` belongs under the input that
 * caused it; everything else is a banner. Keeping that decision here means a
 * new command cannot accidentally render a field problem as a banner, which is
 * how a form ends up telling somebody "something went wrong" while the answer
 * is sitting next to a box they can see.
 */
export function formStateFor(
  failure: Failure,
  values?: Record<string, string>,
): FormState {
  if (failure.fieldErrors) {
    return { fieldErrors: renderFieldErrors(failure.fieldErrors), values };
  }
  return { formError: catalogCopyFor(failure), values };
}
